import { type Card, type Rank, type Suit, cardId } from '../trick/cards.js';

/**
 * Rummy melds, as pure functions over cards. A **set** is three or four cards of one rank in different suits. A **run** is three or more
 * consecutive cards of one suit; the ace is low (A-2-3) and, when `aceHigh` is on, may also be high (Q-K-A). A run never wraps (K-A-2) in the full deck.
 *
 * **Wrap-around** (an option, `wrapFrom`): the ranks of the deck form a ring, so a run may wrap (K-A-2 with the full deck, Q-K-A-8-9
 * with Big Cards, 8 to Ace) and may be up to the whole ring long. The ace then sits between king and the lowest card, so `aceHigh`
 * only changes what the ace is worth in a hand. Big Cards turns it on by default.
 */
export type MeldKind = 'set' | 'run';

export interface MeldShape {
  readonly kind: MeldKind;
  /** Sets are in suit order; runs are in ascending order (an ace first when low, last when high). */
  readonly cards: readonly Card[];
}

/**
 * The wrap-around option, passed as `wrapFrom`: the lowest rank of the deck when runs wrap around the ring of its ranks
 * (2..Ace for the full deck, 8..Ace for Big Cards), or undefined when they do not.
 */
export const wraps = (wrapFrom: Rank | undefined): wrapFrom is Rank => wrapFrom !== undefined;

/** The rank `by` steps along the ring of ranks from `rank` (negative goes down), wrapping within minRank..14. */
export function ringStep(rank: number, by: number, minRank: Rank): number {
  const size = 15 - minRank;
  return minRank + ((((rank - minRank + by) % size) + size) % size);
}

/** How far apart two ranks are along the ring (or plain distance when runs do not wrap). */
export function rankGap(a: number, b: number, wrapFrom: Rank | undefined): number {
  const d = Math.abs(a - b);
  return wrapFrom === undefined ? d : Math.min(d, 15 - wrapFrom - d);
}

const SUIT_ORDER: Record<Suit, number> = { C: 0, D: 1, H: 2, S: 3 };

/** A card's rank for running purposes: the ace is 1 (low) or 14 (high); every other card is its rank. */
const runRank = (card: Card, aceLow: boolean): number => (card.rank === 14 && aceLow ? 1 : card.rank);

/** What a card is worth in a hand when someone else goes out: face cards 10, pips as printed, the ace 1 (15 when it may be played high). */
export function cardValue(card: Card, aceHigh = false): number {
  if (card.rank === 14) return aceHigh ? 15 : 1;
  return card.rank >= 11 ? 10 : card.rank;
}

export const handValue = (cards: readonly Card[], aceHigh = false): number => cards.reduce((a, c) => a + cardValue(c, aceHigh), 0);

export function isSet(cards: readonly Card[]): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  const rank = cards[0]?.rank;
  if (!cards.every((c) => c.rank === rank)) return false;
  return new Set(cards.map((c) => c.suit)).size === cards.length;
}

/** The cards in run order if they form a valid run, else null. */
export function orderRun(cards: readonly Card[], aceHigh = false, wrapFrom?: Rank): Card[] | null {
  if (cards.length < 3) return null;
  const suit = cards[0]?.suit;
  if (!cards.every((c) => c.suit === suit)) return null;
  if (wraps(wrapFrom)) return orderRingRun(cards, wrapFrom);
  for (const aceLow of aceHigh ? [true, false] : [true]) {
    const sorted = [...cards].sort((a, b) => runRank(a, aceLow) - runRank(b, aceLow));
    let ok = true;
    for (let i = 1; i < sorted.length; i++) {
      if (runRank(sorted[i] as Card, aceLow) !== runRank(sorted[i - 1] as Card, aceLow) + 1) {
        ok = false;
        break;
      }
    }
    if (ok) return sorted;
  }
  return null;
}

/** A wrap-around run: n distinct ranks of the ring that are consecutive from some starting rank. Starts after the biggest gap. */
function orderRingRun(cards: readonly Card[], minRank: Rank): Card[] | null {
  const size = 15 - minRank;
  if (cards.length > size) return null;
  const sorted = [...cards].sort((a, b) => a.rank - b.rank);
  if (new Set(sorted.map((c) => c.rank)).size !== sorted.length) return null;
  if (sorted.length === size) return sorted;
  let start = -1;
  for (let i = 0; i < sorted.length; i++) {
    const next = sorted[(i + 1) % sorted.length] as Card;
    const forward = (((next.rank - (sorted[i] as Card).rank) % size) + size) % size;
    if (forward > 1) {
      if (start !== -1) return null; // two gaps: not one stretch of the ring
      start = (i + 1) % sorted.length;
    }
  }
  return start === -1 ? null : [...sorted.slice(start), ...sorted.slice(0, start)];
}

/** Is this group of cards a legal meld? */
export function validateMeld(cards: readonly Card[], aceHigh = false, wrapFrom?: Rank): MeldShape | null {
  if (isSet(cards)) return { kind: 'set', cards: [...cards].sort((a, b) => SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit]) };
  const run = orderRun(cards, aceHigh, wrapFrom);
  return run ? { kind: 'run', cards: run } : null;
}

/** The meld's cards after adding `card`, or null when it cannot be laid off there. */
export function layOffOnto(meld: MeldShape, card: Card, aceHigh = false, wrapFrom?: Rank): readonly Card[] | null {
  if (meld.cards.some((c) => c.rank === card.rank && c.suit === card.suit)) return null;
  if (meld.kind === 'set') {
    if (meld.cards.length >= 4 || card.rank !== meld.cards[0]?.rank) return null;
    const next = [...meld.cards, card];
    return isSet(next) ? next.sort((a, b) => SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit]) : null;
  }
  return orderRun([...meld.cards, card], aceHigh, wrapFrom);
}

const key = (cards: readonly Card[]): string =>
  cards
    .map(cardId)
    .sort()
    .join(',');

/** Every legal meld that can be formed from these cards (each distinct group once). */
export function allMelds(hand: readonly Card[], aceHigh = false, wrapFrom?: Rank): MeldShape[] {
  const out = new Map<string, MeldShape>();
  const add = (cards: readonly Card[]) => {
    const meld = validateMeld(cards, aceHigh, wrapFrom);
    if (meld) out.set(key(cards), meld);
  };
  // Sets: three or four of a rank.
  const byRank = new Map<number, Card[]>();
  for (const c of hand) byRank.set(c.rank, [...(byRank.get(c.rank) ?? []), c]);
  for (const group of byRank.values()) {
    if (group.length < 3) continue;
    add(group);
    if (group.length === 4) for (let skip = 0; skip < 4; skip++) add(group.filter((_, i) => i !== skip));
  }
  // Runs: every window of three or more consecutive cards in a suit, with the ace low and (optionally) high.
  for (const suit of ['C', 'D', 'H', 'S'] as const) {
    const cards = hand.filter((c) => c.suit === suit);
    if (wraps(wrapFrom)) {
      // On a ring a run is any subset of 3+ ranks of the suit; the hand holds at most 7 per suit, so try them all.
      for (let mask = 1; mask < 1 << cards.length; mask++) {
        const pick = cards.filter((_, i) => mask & (1 << i));
        if (pick.length >= 3) add(pick);
      }
      continue;
    }
    for (const aceLow of aceHigh ? [true, false] : [true]) {
      const sorted = [...cards].sort((a, b) => runRank(a, aceLow) - runRank(b, aceLow));
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 2; j < sorted.length; j++) {
          const window = sorted.slice(i, j + 1);
          if (runRank(window[window.length - 1] as Card, aceLow) - runRank(window[0] as Card, aceLow) !== window.length - 1) break;
          add(window);
        }
      }
    }
  }
  return [...out.values()];
}

export interface MeldPlan {
  /** Disjoint melds to lay down, most valuable first. */
  readonly melds: readonly MeldShape[];
  /** The cards left over. */
  readonly deadwood: readonly Card[];
  /** The total value of the cards in the melds. */
  readonly meldedValue: number;
}

/**
 * The best way to lay down melds from a hand: disjoint melds that take the most card value out of the hand (then the most cards).
 * Exhaustive over the cards (a hand is at most about a dozen), memoised on which cards are still free.
 */
export function bestMeldPlan(hand: readonly Card[], aceHigh = false, wrapFrom?: Rank): MeldPlan {
  const n = hand.length;
  const index = new Map(hand.map((c, i) => [cardId(c), i]));
  const melds = allMelds(hand, aceHigh, wrapFrom).map((m) => ({ meld: m, mask: m.cards.reduce((a, c) => a | (1 << (index.get(cardId(c)) as number)), 0) }));
  const byCard: { meld: MeldShape; mask: number }[][] = Array.from({ length: n }, () => []);
  for (const m of melds) for (let i = 0; i < n; i++) if (m.mask & (1 << i)) byCard[i]?.push(m);
  const memo = new Map<number, { value: number; cards: number; picks: MeldShape[] }>();
  const solve = (free: number): { value: number; cards: number; picks: MeldShape[] } => {
    if (free === 0) return { value: 0, cards: 0, picks: [] };
    const cached = memo.get(free);
    if (cached) return cached;
    const i = 31 - Math.clz32(free & -free); // the lowest free card
    let best = solve(free & ~(1 << i)); // leave it as deadwood
    for (const m of byCard[i] ?? []) {
      if ((m.mask & free) !== m.mask) continue;
      const sub = solve(free & ~m.mask);
      const value = sub.value + handValue(m.meld.cards, aceHigh);
      const cards = sub.cards + m.meld.cards.length;
      if (value > best.value || (value === best.value && cards > best.cards)) best = { value, cards, picks: [m.meld, ...sub.picks] };
    }
    memo.set(free, best);
    return best;
  };
  const best = solve(n === 0 ? 0 : (1 << n) - 1);
  const used = new Set(best.picks.flatMap((m) => m.cards.map(cardId)));
  return {
    melds: [...best.picks].sort((a, b) => handValue(b.cards, aceHigh) - handValue(a.cards, aceHigh)),
    deadwood: hand.filter((c) => !used.has(cardId(c))),
    meldedValue: best.value,
  };
}
